import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from '@/components/ui/toast'
import { useUpdateTaskStatus } from '@/hooks/useTasks'
import { getApiErrorMessage } from '@/services/api'
import { type TaskStatus } from '@/types'

const dialogContentClass =
  'w-[calc(100%-2rem)] max-w-[480px] gap-0 border-[#e7e9ef] bg-white p-0 shadow-[0_1px_2px_-1px_rgba(38,45,66,0.05),0_8px_8px_-1px_rgba(38,45,66,0.1)] sm:rounded-2xl'
const fieldLabelClass =
  'inline-flex items-center gap-1 text-xs font-medium leading-4 text-[#171a20]'
const selectTriggerClass =
  'h-10 rounded-xl border-transparent bg-[#f5f6fa] py-0 pl-4 pr-3 text-sm font-normal text-[#6c7285] shadow-none ring-0 ring-offset-0 focus:border-[#e7e9ef] focus:bg-white focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 data-[placeholder]:text-[#6c7285] [&>span]:font-normal [&>svg]:text-[#6c7285] [&>svg]:opacity-100'
const cancelButtonClass =
  'h-11 rounded-xl border-[#e7e9ef] bg-white px-3 py-3 text-sm font-medium leading-none text-[#171a20] shadow-none hover:bg-[#f8f9fb]'
const submitButtonClass =
  'h-11 rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] px-3 py-3 text-sm font-medium leading-none text-white shadow-none hover:bg-[#e6511c]'

interface Props {
  taskId: string
  current: TaskStatus
  initial?: TaskStatus
  open: boolean
  onClose: () => void
}

// 2.9.5 — Update task status (forward / reverse transitions allowed).
const STATUS_OPTIONS: TaskStatus[] = [
  'todo',
  'in_progress',
  'done',
  'cancelled',
]

export function UpdateTaskStatusDialog({
  taskId,
  current,
  initial,
  open,
  onClose,
}: Props) {
  const { t } = useTranslation('tasks')
  const [status, setStatus] = useState<TaskStatus>(current)
  const mut = useUpdateTaskStatus(taskId)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (open) setStatus(initial ?? current)
  }, [open, current, initial])

  const onUpdate = async () => {
    // 2.9.5-013 — unchanged status: no log, just close.
    if (status === current) {
      onClose()
      return
    }
    try {
      await mut.mutateAsync({ status })
      toast({ variant: 'success', title: t('updateStatusDialog.updated') })
      onClose()
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('updateStatusDialog.updateError')),
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent
        className={dialogContentClass}
        overlayClassName='bg-black/70 backdrop-blur-[2px]'
        showCloseButton={false}
      >
        <div className='flex flex-col gap-6 p-8'>
          <DialogHeader className='text-left'>
            <DialogTitle className='font-heading text-base font-semibold leading-[1.2] tracking-normal text-[#171a20]'>
              {t('updateStatusDialog.title')}
            </DialogTitle>
          </DialogHeader>

          <div className='space-y-1'>
            <Label className={fieldLabelClass}>
              {t('updateStatusDialog.status')}
              <span className='text-[#d63b33]'>*</span>
            </Label>
            <Select
              value={status}
              onValueChange={v => setStatus(v as TaskStatus)}
            >
              <SelectTrigger className={selectTriggerClass}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className='rounded-xl border-[#e7e9ef]'>
                {STATUS_OPTIONS.map(s => (
                  <SelectItem
                    key={s}
                    value={s}
                    className='rounded-lg py-2.5 pl-8 pr-3 text-sm font-normal text-[#171a20]'
                  >
                    {t(`enums:taskStatus.${s}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter className='flex-row justify-end gap-4 space-x-0'>
            <Button
              type='button'
              variant='outline'
              className={cancelButtonClass}
              onClick={onClose}
            >
              {t('common:actions.cancel')}
            </Button>
            <Button
              type='button'
              className={submitButtonClass}
              disabled={mut.isPending}
              onClick={onUpdate}
            >
              {t('updateStatusDialog.update')}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default UpdateTaskStatusDialog
